import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import cn from 'classnames';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import ArrowLeftIcon from '@wix/da-ds/pkg/Icons/16x16/ArrowLeft';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import FormStepsDisplay from './_partials/FormStepsDisplay';
import FirstStep from './_partials/FirstStep';
import SecondStep from './_partials/SecondStep';
import CoverImageField from './fields/CoverImageField';
import { OriginalDeviationFormFields } from './types';

import s from './OriginalDeviationPopup.scss';

const FINAL_STEP = 2;

interface Props {
  onRequestClose: () => void;
}

const OriginalDeviationPopup: React.FC<Props> = ({ onRequestClose }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const isMobile = useContext(MobileContext);

  const formContext = useForm<OriginalDeviationFormFields>({
    mode: 'onSubmit',
    defaultValues: {
      deliveryTime: 7,
      displayResolution: 'original',
      addWatermark: false,
    },
  });

  const handleContinueClick = useCallback(() => {
    setStep(step + 1);
  }, [step]);

  return (
    <Modal
      isOpen
      onRequestClose={onRequestClose}
      heading={t('originalDeviationPopup.heading')}
      subheading={<FormStepsDisplay currentStep={step} />}
      withScrollableContentWrapper={isMobile}
      showHeaderBorder
      showFooterBorder
      footerLeft={[
        step > 1 && (
          <TextButton
            key="cancel"
            variant="main"
            size="medium"
            icon={ArrowLeftIcon}
            onClick={() => setStep(step - 1)}
          >
            {t('common.back')}
          </TextButton>
        ),
      ]}
      footerRight={[
        <TextButton
          key="cancel"
          variant="main"
          size="medium"
          onClick={onRequestClose}
        >
          {t('common.cancel')}
        </TextButton>,
        step === FINAL_STEP ? (
          <BrandedButton
            key="confirm"
            variant="brand-green"
            size="medium"
            onClick={onRequestClose}
          >
            {t('common.submit')}
          </BrandedButton>
        ) : (
          <BrandedButton
            key="confirm"
            variant="brand-green"
            size="medium"
            onClick={handleContinueClick}
          >
            {t('common.continue')}
          </BrandedButton>
        ),
      ]}
    >
      <FormProvider {...formContext}>
        <div className={cn(s['content'], s[`step-${step}`])}>
          <div className={s['main-area']}>
            {step === 1 && <FirstStep />}
            {step === 2 && <SecondStep />}
          </div>
          <div className={s['preview-area']}>
            <CoverImageField />
          </div>
        </div>
      </FormProvider>
    </Modal>
  );
};
OriginalDeviationPopup.displayName = 'OriginalDeviationPopup';

export default OriginalDeviationPopup;
